/**
 * Sign-in screen, shared by both applications.
 *
 * ## One message for every failure
 *
 * A wrong employee id, a wrong password, and a suspended account all render the same sentence. The
 * server already refuses to distinguish them — see `INVALID` in `app/api/auth.py` — and a client
 * that inferred more from the status code would hand back the enumeration oracle the server was
 * careful not to give. Throttling is the one exception: "try again in N minutes" is not a hint about
 * whether the account exists, and withholding it only makes a locked-out employee retry into a
 * longer lockout.
 *
 * ## Centred, not full-width
 *
 * The card is capped and centred because a login form stretched across a 1440px branch monitor puts
 * the label and the field at opposite ends of the desk. Logical properties throughout, so the
 * Arabic layout is the mirror rather than a second stylesheet.
 *
 * ## It wears the same clothes as the rest of the product
 *
 * This screen was written before the design was implemented and kept the scaffold's chrome: a
 * bordered header strip with the bank's name set as text, the boxed language toggle, and a card
 * with none of the design's weight. It was the first thing an employee saw and the only screen
 * that did not look like the product.
 *
 * It now takes its furniture from the same places every other screen does — the wordmark from
 * `SideRail`'s header, the disclaimer-and-confidentiality card from its footer, the compact
 * language toggle, the accent button. Nothing here is a second copy: the strings are the shared
 * `disclaimer.*` keys, so the confidentiality marking cannot say one thing at sign-in and another
 * once you are inside.
 */

import type { TFunction } from "i18next";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { LanguageToggle } from "../components/LanguageToggle";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { PasswordInput } from "../ui/PasswordInput";
import { useSession } from "./SessionProvider";

import logoUrl from "../assets/nab-logo.png";

export interface LoginPageProps {
  /** Application name, so the employee can see which of the two they are signing in to. */
  title: string;
}

export function LoginPage({ title }: LoginPageProps): JSX.Element {
  const { t } = useTranslation("auth");
  const { t: common } = useTranslation();
  const { signIn, mode } = useSession();
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const passwordLogin = mode?.password_login ?? true;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (!employeeId.trim() || !password) {
      setError(t("login.required"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await signIn(employeeId.trim(), password);
    } catch (caught) {
      setError(messageFor(caught, t));
      // Cleared on failure so a mistyped password is not resubmitted by a second Enter, and so it
      // does not sit in the DOM after the attempt that failed.
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface p-6">
      <main className="flex w-full max-w-form flex-col items-center">
        {/* The wordmark, at the size the rail carries it. An employee arrives here from a bookmark
            or a link and this is the only thing on screen that says whose system it is. */}
        <img
          src={logoUrl}
          alt={t("brand.bank", { ns: "common" })}
          className="mb-6 h-12 w-auto max-w-[240px] object-contain"
        />

        <div className="w-full rounded-xl border border-borderStrong bg-surfaceRaised p-6 shadow-sm">
          <h1 className="text-heading-md text-textPrimary">{t("login.heading")}</h1>
          <p className="mt-1 text-body-sm text-textMuted">{title}</p>
          <p className="mt-3 text-body-sm text-textMuted">{t("login.subheading")}</p>

          {passwordLogin ? (
            <form className="mt-6 flex flex-col gap-4" onSubmit={submit} noValidate>
              <Input
                label={t("login.employeeId")}
                value={employeeId}
                onChange={(event) => setEmployeeId(event.target.value)}
                autoComplete="username"
                // The first field on a page whose only purpose is this form.
                autoFocus
                required
                dir="ltr"
              />
              <PasswordInput
                label={t("login.password")}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                dir="ltr"
                {...(error ? { error } : {})}
                showLabel={t("password.show")}
                hideLabel={t("password.hide")}
              />
              {/* Not disabled until something is typed. A greyed-out primary button is the first
                  thing on the screen and reads as a broken page rather than as a form waiting for
                  input; the form refuses an empty submit instead, which says the same thing at the
                  moment it is useful. */}
              <Button type="submit" variant="primary" loading={busy}>
                {busy ? t("login.signingIn") : t("login.signIn")}
              </Button>
            </form>
          ) : (
            <p className="mt-6 text-body-md text-textPrimary">{t("login.useIdentityProvider")}</p>
          )}
        </div>

        {/* The same card the rail carries at its foot, and the same strings. A bank's
            confidentiality marking must not appear only after sign-in. */}
        <div className="mt-4 flex w-full flex-col overflow-hidden rounded-[14px] border border-borderStrong bg-surfaceRaised shadow-sm">
          <div className="flex flex-col gap-2 p-3">
            <p className="text-label-sm font-medium leading-snug text-textPrimary">
              {common("disclaimer.accuracy")}
            </p>
            <div className="flex flex-col gap-1 border-t border-surfaceSunken pt-2 text-[9.5px] font-bold uppercase tracking-wider text-textMuted">
              <span>{common("disclaimer.confidential")}</span>
              <span>{common("disclaimer.poweredBy")}</span>
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-borderStrong bg-surface px-3 py-2">
            {/* Before sign-in, not after: an employee who reads Arabic needs the form in Arabic. */}
            <LanguageToggle compact />
          </div>
        </div>
      </main>
    </div>
  );
}

/** Maps a failure to a sentence. See the module docstring on why almost everything collapses. */
function messageFor(caught: unknown, t: TFunction<"auth">): string {
  if (caught instanceof ApiError) {
    if (caught.status === 429) {
      return typeof caught.detail === "string" ? caught.detail : t("login.throttled");
    }
    // 403 is the one refusal with a reason worth passing through: the password was correct and the
    // account is waiting for an administrator. Self-registration is gone, but accounts created
    // before it was removed can still be in that state, and collapsing it into "those credentials
    // do not match" would send someone back to try a password that is fine.
    if (caught.status === 403) {
      return typeof caught.detail === "string" ? caught.detail : t("login.pending");
    }
    if (caught.status === 400 || caught.status === 501) {
      return typeof caught.detail === "string" ? caught.detail : t("login.unavailable");
    }
    if (caught.status === 401) return t("login.invalid");
  }
  return t("login.unreachable");
}
